/**
 * ProposalPreview — modal that plays a proposal template like the
 * proposee would experience it.
 *
 *   - Cinematic intro (heart-pulse + proposee name reveal + magnetic Begin)
 *   - Slides with chosen page-transition + light-leak flash + film grain
 *   - Letter-by-letter question reveal + beat-pulsing FINAL slide
 *   - Cinematic "respond" dialog at the end (DATE) — heartbeat YES, playful NO
 *   - Auto-advance after each slide's `duration`; supports keyboard nav,
 *     pause, restart, and manual prev/next.
 *
 * Architecture (post-redesign):
 *
 *   runtime/usePlayerMachine       — player state machine + schedulers
 *   runtime/usePreviewIo           — catalogue fetch
 *   layers/*                       — single-purpose render pieces
 *   stage/Stage                    — phone-frame container
 *   transitions/PageTransitionStage — between-slide animation
 *   opening/OpeningRegistry        — top-level opening animation
 *   slides/variants/*              — per-slide-type JSX
 *   particles/ParticleOverlay      — ambient particles + ambient-orbs
 *
 * The modal itself stays a thin orchestrator: it wires the hooks
 * together, renders the chrome (modal frame + controls + dropdowns),
 * and composes the layered tree.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, ChevronLeft, ChevronRight, RotateCcw, X, Sparkles, Wand2 } from "lucide-react";
import { PreviewSlide, type PreviewSlideData } from "./PreviewSlide";
import { AMBIENT_EFFECTS } from "./effects-catalogue";
import { useAmbientCatalog } from "./useAmbientCatalog";
import { type ProposalTransition } from "@/lib/transitions/catalogue";
import { cn } from "@/lib/utils";

import { usePlayerMachine } from "./runtime/usePlayerMachine";
import { usePreviewIo } from "./runtime/usePreviewIo";
import { Stage } from "./stage/Stage";
import { DeckBackgroundLayer, ChromeLayer } from "./layers";
import {
  PageTransitionStage,
  resolveActiveTransition,
  buildTransitionSignature,
} from "./transitions";
import { resolveOpening, shouldMountOpening } from "./opening";
import { ParticleOverlay } from "./particles/ParticleOverlay";
import { FinaleOverlay } from "./FinaleOverlay";
import { BackgroundMusic } from "../../viewer/BackgroundMusic";

export type ProposalType = "MARRIAGE" | "RELATIONSHIP" | "DATE";

interface ProposalPreviewProps {
  open: boolean;
  onClose: () => void;
  /** Title shown at the top of the modal. */
  title: string;
  /** Slide list — sourced from the editor's form state. */
  slides: PreviewSlideData[];
  /** For DATE templates we show a "respond" dialog at the end. */
  proposalType?: ProposalType;
  /** Optional: name of the proposee shown in the cinematic intro. */
  proposeeName?: string;
  /** Top-level opening animation. Defaults to 'cinematic-curtain'. */
  openingAnimation?: string;
  /** Top-level default background. Defaults to 'floating-heart-bokeh'. */
  defaultBackground?: string;
  /**
   * Page transition (slide → slide) code from ProposalTransition.
   * `null` = use the built-in iris-cut default. Wires to a dropdown
   * in the modal header so admins can audition each transition.
   */
  pageTransitionId?: string | null;
  /** Fired when the admin picks a new transition from the dropdown. */
  onPageTransitionChange?: (id: string | null) => void;
  /**
   * Viewer tier — gates PRO transitions for FREE viewers. Defaults to
   * `'PRO'` so admins always see what a paid viewer would see.
   */
  tier?: 'FREE' | 'PRO';
  /**
   * Background music URL for the deck (in-built /audio/*.mp3 or an
   * AI-generated hosted URL). The admin preview plays this so the
   * template author can audit the selection without leaving the page.
   */
  musicUrl?: string | null;
  /** Mute toggle bound to the preview surface. */
  isMuted?: boolean;
}

export function ProposalPreview({
  open,
  onClose,
  title,
  slides,
  proposalType,
  proposeeName,
  openingAnimation = "cinematic-curtain",
  defaultBackground = "floating-heart-bokeh",
  pageTransitionId = null,
  onPageTransitionChange,
  tier = 'PRO',
  musicUrl = null,
  isMuted = false,
}: ProposalPreviewProps) {
  // Refresh the admin-managed AmbientEffect catalogue so the deck /
  // slide previews show the latest enabled codes (incl. the new
  // concept 1 + concept 2 PRO effects). The hook pushes rows into the
  // module-level LIVE_EFFECTS cache.
  useAmbientCatalog();
  const player = usePlayerMachine({
    open,
    slides,
    openingAnimation,
    proposalType,
    onClose,
  });
  const io = usePreviewIo(open);

  // Track the previous "effective" transition id so we can remount the
  // page-transition stage when the admin flips the picker while open.
  // Mirrors the legacy `playbackKey` behaviour but scoped to the
  // transition.
  const transitionSig = useMemo(
    () => buildTransitionSignature(slides, pageTransitionId),
    [slides, pageTransitionId],
  );
  const [transitionVersion, setTransitionVersion] = useState(0);
  useEffect(() => {
    if (!open) return;
    setTransitionVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionSig]);

  // Slide-direction-aware stageKey: page-transition stage keys its
  // animations off this string so that flipping forward/back replays
  // the right direction.
  const stageKey = `${player.index}-${player.playbackKey}-${transitionVersion}`;

  // Pick a sensible default transition for the deck: any enabled row,
  // preferring `css-liquid` (Liquid Fill) so the preview always plays
  // a visually rich animation. `css-curtain` and `css-zoom` were both
  // retired from the catalogue, so the fallback can't reference them.
  const defaultTransitionId = useMemo(() => {
    if (io.transitions.length === 0) return null;
    const preferred = io.transitions.find((t) => t.id === 'css-liquid');
    if (preferred) return preferred.id;
    const enabled = io.transitions.find((t) => t.isEnabled);
    return enabled?.id ?? io.transitions[0].id;
  }, [io.transitions]);

  // Resolve the catalogue row for the currently-displayed slide (per-slide
  // override → deck-level → first enabled transition).
  const activeTransition = useMemo(
    () =>
      resolveActiveTransition({
        slideTransitionId: player.currentSlide?.pageTransitionId ?? null,
        deckTransitionId: pageTransitionId ?? defaultTransitionId ?? null,
        transitions: io.transitions,
      }),
    [player.currentSlide, pageTransitionId, defaultTransitionId, io.transitions],
  );

  // The label shown in the picker header reflects the effective transition.
  const effectiveTransitionId =
    player.currentSlide?.pageTransitionId ?? pageTransitionId ?? defaultTransitionId ?? null;

  if (!open) return null;

  const isFinalSlide = player.isFinalSlide;
  const OpeningComponent = resolveOpening(openingAnimation);
  const showOpening = shouldMountOpening(openingAnimation) && player.curtainPending;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur"
      role="dialog"
      aria-modal="true"
      aria-label="Proposal preview"
    >
      <BackgroundMusic
        url={musicUrl ?? null}
        isMuted={isMuted}
        isPlaying={open && !player.showIntro && player.isPlaying}
      />
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 text-white">
        <div className="flex items-center gap-3 min-w-0">
          <span className="rounded-md bg-white/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide">
            Preview
          </span>
          <span className="truncate text-sm font-medium text-white/80">{title}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/60">
          <span>
            {player.showIntro
              ? "Intro"
              : `${player.index} / ${slides.length} slide${slides.length === 1 ? "" : "s"}`}
          </span>
          <span>·</span>
          <span>{proposalType ?? "—"}</span>
          <span>·</span>
          <span className="hidden sm:inline">← → space P esc</span>

          <TransitionPicker
            transitions={io.transitions}
            transitionId={effectiveTransitionId}
            onChange={(id) => onPageTransitionChange?.(id)}
          />

          <button
            type="button"
            onClick={() => {
              // Effect showcase is local state — switch from the
              // preview surface (the editor exposes its own
              // wand-button to open it; here we just close).
              onClose();
            }}
            className="ml-2 rounded-lg p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Toggle effect showcase"
            title="Toggle effect showcase"
          >
            <Wand2 className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 rounded-lg p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Phone-frame stage */}
      <div className="flex flex-1 items-center justify-center px-4 py-6">
        {slides.length === 0 ? (
          <div className="text-white/60">No slides to preview yet — add some first.</div>
        ) : (
          <Stage>
            <ChromeLayer>
              {/* DeckBackgroundLayer sits at z=1 (via DeckBackgroundLayer).
                 ambient-orbs hooks here automatically when the chosen
                 code matches. */}
              <DeckBackgroundLayer code={defaultBackground} intensity={0.6} />

              {/* Page transition stage — wraps the slide render so the
                  chosen transition actually plays between slides.
                  Falls back to [data-t="iris"] when `transition` is null. */}
              {player.showIntro || !player.currentSlide ? (
                <PreviewSlide
                  slide={player.currentSlide ?? slides[0]}
                  playbackKey={player.playbackKey}
                  lightLeakGold={isFinalSlide}
                />
              ) : (
                <PageTransitionStage
                  slideKey={stageKey}
                  direction={player.direction}
                  transition={activeTransition}
                  oldLayer={
                    player.prevSlide ? (
                      <PreviewSlide
                        slide={player.prevSlide}
                        playbackKey={`prev-${player.playbackKey}`}
                      />
                    ) : null
                  }
                  newLayer={
                    <PreviewSlide
                      slide={player.currentSlide}
                      playbackKey={player.playbackKey}
                      lightLeakGold={isFinalSlide}
                    />
                  }
                />
              )}

              {/* Top slide-counter pill */}
              <div className="pointer-events-none absolute left-0 right-0 top-3 z-10 flex justify-center">
                <span className="rounded-full bg-black/50 px-3 py-1 text-xs font-medium text-white/90 backdrop-blur">
                  {player.index} / {slides.length}
                </span>
              </div>

              {/* Auto-advance progress bar */}
              {!player.showIntro && (player.currentSlide?.duration ?? 0) > 0 && (
                <div className="pointer-events-none absolute inset-x-3 top-10 z-10 overflow-hidden rounded-full bg-white/10 backdrop-blur">
                  <div
                    key={`progress-${player.index}-${player.playbackKey}-${player.isPlaying ? 'play' : 'pause'}`}
                    className="preview-progress-bar"
                    style={{
                      animationDuration: player.isPlaying
                        ? `${player.currentSlide!.duration}s`
                        : '0s',
                      animationPlayState: player.isPlaying ? 'running' : 'paused',
                    }}
                  />
                </div>
              )}

              {/* Respond dialog for DATE templates */}
              {player.showRespondDialog && !player.showIntro && proposalType && (
                <CinematicRespondDialog
                  proposalType={proposalType}
                  onAccept={onClose}
                  onClose={onClose}
                  onRestart={player.restart}
                />
              )}

              {/* Player controls (hidden during intro + respond dialog +
                 finale) */}
              {!player.showIntro && !player.showRespondDialog && !player.isComplete && (
                <PlayerControls
                  isPlaying={player.isPlaying}
                  canPrev={player.index > 1}
                  canNext={
                    player.index - 1 < slides.length - 1 || proposalType === 'DATE'
                  }
                  onTogglePlay={player.togglePlay}
                  onPrev={player.prev}
                  onNext={player.next}
                  onRestart={player.restart}
                />
              )}

              {/* Cinematic intro overlay (replaces the deck view while
                  the player is in the intro phase) */}
              {player.showIntro && (
                <CinematicIntroOverlay
                  phase={player.introBeat}
                  proposeeName={proposeeName || 'you'}
                  proposalType={proposalType}
                  onBegin={player.begin}
                />
              )}
            </ChromeLayer>

            {/* Opening animation sits OUTSIDE the chrome layer so the
               curtain can paint over the rounded corners + shadow. */}
            {showOpening && <OpeningComponent active={player.curtainPending} />}

            {/* Finale overlay — sits outside ChromeLayer so it can
               cover the stage entirely without z-index collisions. */}
            {player.isComplete && (
              <FinaleOverlay
                proposalType={proposalType}
                proposeeName={proposeeName}
                onRestart={player.restart}
                onClose={onClose}
              />
            )}
          </Stage>
        )}
      </div>
    </div>
  );
}

function PlayerControls({
  isPlaying,
  canPrev,
  canNext,
  onTogglePlay,
  onPrev,
  onNext,
  onRestart,
}: {
  isPlaying: boolean;
  canPrev: boolean;
  canNext: boolean;
  onTogglePlay: () => void;
  onPrev: () => void;
  onNext: () => void;
  onRestart: () => void;
}) {
  return (
    <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-3 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4 pb-6">
      <button
        type="button"
        onClick={onRestart}
        className="rounded-full bg-white/10 p-2.5 text-white/90 backdrop-blur transition hover:bg-white/20"
        aria-label="Restart from beginning"
        title="Restart from beginning"
      >
        <RotateCcw className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onPrev}
        disabled={!canPrev}
        className="rounded-full bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20 disabled:opacity-30"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={onTogglePlay}
        className="rounded-full bg-merry-pink p-4 text-white shadow-lg transition hover:bg-merry-pink/90"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-[1px]" />}
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={!canNext}
        className="rounded-full bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20 disabled:opacity-30"
        aria-label="Next slide"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
      <div className="ml-2 text-xs text-white/60">
        <kbd className="rounded bg-white/10 px-1.5 py-0.5">space</kbd>
      </div>
    </div>
  );
}

/**
 * CinematicIntroOverlay — the beat-by-beat intro that plays before the
 * first slide. Mirrors the web frontend's `CinematicIntroPhase.tsx`:
 *   0.4s  heart begins pulsing
 *   1.2s  "Someone has a moment for you"
 *   1.8s  "A moment for" reveal
 *   2.4s  [proposeeName] scale-in
 *   ↓      user taps Begin → slide deck starts
 *
 * The center glyph adapts to the proposal type so the admin sees the
 * correct symbol for the template they're previewing:
 *   - MARRIAGE    → ring
 *   - RELATIONSHIP → rose
 *   - DATE        → calendar + champagne
 */
function CinematicIntroOverlay({
  phase,
  proposeeName,
  proposalType,
  onBegin,
}: {
  phase: number;
  proposeeName: string;
  proposalType?: ProposalType;
  onBegin: () => void;
}) {
  const glyph = introGlyph(proposalType);
  const title = introTitle(proposalType);

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col items-center justify-center overflow-hidden rounded-[2.5rem] pointer-events-auto"
      style={{
        background:
          'radial-gradient(circle at 50% 40%, rgba(20, 6, 14, 0.55) 0%, rgba(0, 0, 0, 0.85) 70%, rgba(0, 0, 0, 0.95) 100%)',
      }}
    >
      {/* Warm breathing backdrop — tinted to the proposal type */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 50% 45%, ${glyph.backdropTint} 0%, transparent 55%)`,
          animation: 'candle-breathe 4s ease-in-out infinite',
        }}
      />

      {/* Light leak on phase 2 */}
      {phase >= 2 && (
        <div
          key={`intro-leak-${phase}`}
          className="preview-light-leak"
          style={{ zIndex: 21 }}
        />
      )}

      {/* Always-on cinema overlays */}
      <div className="preview-vignette" style={{ zIndex: 22 }} />
      <div className="preview-film-grain" style={{ zIndex: 23 }} />

      <div className="relative z-30 flex flex-col items-center gap-6 px-6 text-center">
        {/* Proposal-type glyph with shockwave */}
        <div className="relative h-16 w-16">
          {phase >= 1 && <span className="preview-shockwave" style={{ position: 'absolute' }} />}
          <span
            className={`absolute inset-0 inline-flex items-center justify-center rounded-full ring-1 ring-white/15 ${
              phase >= 1 ? 'preview-intro-heart' : 'opacity-0'
            }`}
            style={{
              background: glyph.gradient,
              boxShadow: `0 0 40px ${glyph.glow}`,
            }}
          >
            {glyph.kind === 'single' ? (
              <span className="text-3xl leading-none">{glyph.emoji}</span>
            ) : (
              <CompositeGlyph kind={glyph.kind} />
            )}
          </span>
        </div>

        {/* Phase 1: hint */}
        <p
          className={`text-[0.6rem] uppercase tracking-[0.3em] text-white/55 transition-opacity duration-700 ${
            phase >= 1 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          Someone has a moment for you
        </p>

        {/* Phase 3: "A moment for" */}
        <h1
          className={`text-xl font-light text-white md:text-2xl transition-all duration-700 ${
            phase >= 3 ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-2 blur-sm'
          }`}
        >
          A moment for
        </h1>

        {/* Phase 4: name */}
        <h1
          className={`text-3xl font-black tracking-tight text-white md:text-5xl transition-all duration-1000 ${
            phase >= 4 ? 'opacity-100 scale-100 blur-0' : 'opacity-0 scale-90 blur-md'
          }`}
          style={{
            textShadow:
              '0 0 40px rgba(255,105,180,0.6), 0 4px 30px rgba(0,0,0,0.6)',
          }}
        >
          {proposeeName}
        </h1>

        {/* Begin button */}
        <button
          type="button"
          onClick={onBegin}
          className={`mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-rose-600 px-7 py-3 text-sm font-bold text-white shadow-[0_8px_24px_rgba(244,63,94,0.5)] transition-all duration-700 hover:scale-105 hover:shadow-[0_12px_32px_rgba(244,63,94,0.7)] ${
            phase >= 4 ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-3 opacity-0'
          }`}
        >
          <Sparkles className="h-4 w-4" />
          Begin
          <span className="text-base">→</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Returns the right glyph + colors for the proposal type.
 *   - MARRIAGE    → single ring (💍)
 *   - RELATIONSHIP → single rose (🌹)
 *   - DATE        → composite (📅 + 🍾)
 */
type GlyphKind = "single" | "composite";

interface IntroGlyph {
  kind: GlyphKind;
  emoji: string;
  gradient: string;
  glow: string;
  backdropTint: string;
}

function introGlyph(proposalType?: ProposalType): IntroGlyph {
  switch (proposalType) {
    case 'RELATIONSHIP':
      return {
        kind: 'single',
        emoji: '🌹',
        gradient: 'linear-gradient(135deg, #FF5C94 0%, #D60056 100%)',
        glow: 'rgba(255, 92, 148, 0.55)',
        backdropTint: 'rgba(255, 92, 148, 0.18)',
      };
    case 'DATE':
      return {
        kind: 'composite',
        emoji: '🍹',
        gradient: 'linear-gradient(135deg, #FFB340 0%, #FF5C94 100%)',
        glow: 'rgba(255, 179, 64, 0.55)',
        backdropTint: 'rgba(255, 179, 64, 0.18)',
      };
    case 'MARRIAGE':
    default:
      return {
        kind: 'single',
        emoji: '💍',
        gradient: 'linear-gradient(135deg, #FF3377 0%, #D60056 100%)',
        glow: 'rgba(244, 63, 94, 0.55)',
        backdropTint: 'rgba(255, 105, 180, 0.18)',
      };
  }
}

function introTitle(proposalType?: ProposalType): string {
  switch (proposalType) {
    case 'RELATIONSHIP':
      return 'Will you be mine?';
    case 'DATE':
      return 'Will you join me?';
    case 'MARRIAGE':
    default:
      return 'Will you marry me?';
  }
}

/**
 * CompositeGlyph — for DATE templates, renders a calendar + champagne
 * composition that fits inside the 16×16 round badge.
 */
function CompositeGlyph({ kind }: { kind: GlyphKind }) {
  if (kind !== 'composite') return null;
  return (
    <svg
      viewBox="0 0 32 32"
      width="30"
      height="30"
      aria-hidden
      className="relative"
    >
      {/* Calendar (left) */}
      <g transform="translate(2 6)">
        <rect
          x="0"
          y="2"
          width="14"
          height="14"
          rx="2.5"
          fill="rgba(255,255,255,0.18)"
          stroke="#fff"
          strokeWidth="1.4"
        />
        <line x1="0" y1="6" x2="14" y2="6" stroke="#fff" strokeWidth="1.4" />
        <line x1="3" y1="0" x2="3" y2="4" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
        <line x1="11" y1="0" x2="11" y2="4" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
        {/* Heart on calendar */}
        <path
          d="M7 12.5 C5.6 11 4.8 10.2 4.8 9.1 C4.8 8.3 5.4 7.7 6.2 7.7 C6.7 7.7 7.1 7.95 7.4 8.4 C7.7 7.95 8.1 7.7 8.6 7.7 C9.4 7.7 10 8.3 10 9.1 C10 10.2 9.2 11 7 12.5 Z"
          fill="#FF5C94"
          transform="translate(0 1.2) scale(0.7)"
        />
      </g>
      {/* Champagne flutes (right) */}
      <g transform="translate(18 5)">
        {/* Glass 1 — tilted left */}
        <g transform="rotate(-10 5 14)">
          <path
            d="M1.5 1 L8.5 1 L7.5 11 C7.5 12 6.5 12.5 5 12.5 C3.5 12.5 2.5 12 2.5 11 Z"
            fill="rgba(255,255,255,0.20)"
            stroke="#fff"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <line x1="5" y1="12.5" x2="5" y2="19" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
          <ellipse cx="5" cy="20" rx="3.5" ry="0.8" fill="#fff" />
          <path
            d="M2 4 L8 4 L7.4 10 C7.4 10.5 6.4 10.8 5 10.8 C3.6 10.8 2.6 10.5 2.6 10 Z"
            fill="rgba(255, 204, 0, 0.55)"
          />
        </g>
        {/* Glass 2 — tilted right */}
        <g transform="translate(6 0) rotate(10 5 14)">
          <path
            d="M1.5 1 L8.5 1 L7.5 11 C7.5 12 6.5 12.5 5 12.5 C3.5 12.5 2.5 12 2.5 11 Z"
            fill="rgba(255,255,255,0.20)"
            stroke="#fff"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <line x1="5" y1="12.5" x2="5" y2="19" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
          <ellipse cx="5" cy="20" rx="3.5" ry="0.8" fill="#fff" />
          <path
            d="M2 4 L8 4 L7.4 10 C7.4 10.5 6.4 10.8 5 10.8 C3.6 10.8 2.6 10.5 2.6 10 Z"
            fill="rgba(255, 204, 0, 0.55)"
          />
        </g>
      </g>
    </svg>
  );
}

/**
 * CinematicRespondDialog — the climax dialog the proposee would see.
 * Heartbeat halos + magnetic YES button with pulse ring.
 */
function CinematicRespondDialog({
  proposalType,
  onAccept,
  onClose,
  onRestart,
}: {
  proposalType: ProposalType;
  onAccept: () => void;
  onClose: () => void;
  onRestart: () => void;
}) {
  const title =
    proposalType === 'MARRIAGE'
      ? 'Will you marry me?'
      : proposalType === 'RELATIONSHIP'
      ? 'Will you be mine?'
      : 'Will you join me?';

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center overflow-hidden rounded-[2.5rem] bg-black p-6">
      {/* Heartbeat halos */}
      <div className="preview-ring-halo" style={{ top: '32%' }} aria-hidden />
      <div className="preview-ring-halo" style={{ top: '32%' }} aria-hidden />
      <div className="preview-ring-halo" style={{ top: '32%' }} aria-hidden />

      {/* Cinematic overlays */}
      <div className="preview-vignette" style={{ zIndex: 21 }} />
      <div className="preview-film-grain" style={{ zIndex: 22 }} />

      <div className="relative z-30 flex w-full max-w-xs flex-col items-center gap-5 text-center">
        <h3
          className="text-xl font-black text-white md:text-2xl"
          style={{
            textShadow:
              '0 0 30px rgba(255,105,180,0.5), 0 4px 20px rgba(0,0,0,0.6)',
          }}
        >
          {title}
        </h3>
        <p className="text-xs text-white/55">
          (This is the dialog the proposee would see.)
        </p>
        <div className="flex w-full flex-col gap-2">
          <button
            type="button"
            onClick={onAccept}
            className="preview-yes-btn w-full rounded-full bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600 px-5 py-3 text-sm font-bold text-white"
          >
            <span className="relative z-10 inline-flex items-center gap-2">
              <span className="text-base">💍</span> Yes!
            </span>
          </button>
          <button
            type="button"
            className="w-full rounded-full border border-merry-pink px-4 py-2.5 text-sm font-semibold text-merry-pink transition hover:bg-merry-pink/10"
          >
            Propose new time
          </button>
          <button
            type="button"
            onClick={onRestart}
            className="w-full rounded-full px-4 py-2.5 text-xs font-medium text-white/70 transition hover:bg-white/5"
          >
            ↻ Restart preview
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-full px-4 py-2.5 text-xs font-medium text-white/45 transition hover:bg-white/5"
          >
            ✕ Close preview
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * TransitionPicker — dropdown listing every enabled ProposalTransition
 * row, sorted the same way the management page sorts them. Picking an
 * item triggers the parent's `onChange` and the parent re-mounts the
 * stage so the new transition plays on the next slide advance.
 */
function TransitionPicker({
  transitions,
  transitionId,
  onChange,
}: {
  transitions: ProposalTransition[];
  transitionId: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Group rows by `kind` so admins don't have to scroll past 12 similar
  // FM transitions to find the CSS-only replacement (they often wear
  // duplicate-look names: "3D Cube" vs "3D Cube Rotation").
  const grouped = useMemo(() => {
    const enabled = transitions.filter((t) => t.isEnabled);
    return {
      css: enabled.filter((t) => t.kind === 'css'),
      fm: enabled.filter((t) => t.kind === 'fm'),
    };
  }, [transitions]);

  const selected = transitions.find((t) => t.id === transitionId);
  const selectedLabel = selected
    ? `${selected.emoji ?? ''} ${selected.name}`
    : 'Default reveal';

  // Render a single group section. Renders nothing when the group is empty.
  const renderGroup = (label: string, items: typeof grouped.css) =>
    items.length === 0 ? null : (
      <div className="space-y-0.5">
        <div className="px-2 pt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">
          {label}
        </div>
        {items.map((t) => {
          const active = t.id === transitionId;
          return (
            <button
              key={t.id}
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => {
                onChange(t.id);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-start justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors',
                active
                  ? 'bg-pink-500/15 text-pink-100 ring-1 ring-pink-400/40'
                  : 'text-white/75 hover:bg-white/[0.06] hover:text-white',
              )}
            >
              <span className="min-w-0 truncate">
                <span className="mr-1.5">{t.emoji ?? '•'}</span>
                {t.name}
              </span>
              {t.tier === 'PRO' && <span className="pro-tag">PRO</span>}
            </button>
          );
        })}
      </div>
    );

  return (
    <div ref={ref} className="relative ml-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-white/90 transition hover:border-white/30 hover:bg-white/10"
        aria-label="Pick page transition"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-[11px] uppercase tracking-wider text-white/55">Transition</span>
        <span className="font-medium">{selectedLabel}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          aria-hidden
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          // Tall dropdown so the FM group (15 rows) doesn't get cut off.
          // The CSS group lives at the top because admins reach for it
          // most often when iterating on a template.
          className="spatial-glass absolute right-0 z-50 mt-1.5 flex max-h-[70vh] w-80 flex-col gap-1 overflow-y-auto rounded-xl p-1.5 shadow-2xl"
        >
          {renderGroup('CSS keyframes · lighter weight', grouped.css)}
          {renderGroup('Framer-motion · GPU heavy', grouped.fm)}
        </div>
      )}
    </div>
  );
}

// Suppress unused warnings for tier / catalogue catalog pieces that are
// retained as part of the prop-shape contract.
void AMBIENT_EFFECTS;
void ParticleOverlay;
